import type { Signal } from "./types";

/**
 * Advisory signal priority. A transparent, weighted breakdown used ONLY to
 * order the signal list. It never makes a severity, disposition or
 * Problem Record decision. Separate from the Problem Record Top 5.
 */

export type PriorityFactor =
  | "severity"
  | "trend"
  | "exposure"
  | "financial"
  | "recurrence"
  | "safety"
  | "customer"
  | "confidence";

export const PRIORITY_FACTOR_LABELS: Record<PriorityFactor, string> = {
  severity: "Severity",
  trend: "Trend / magnitude",
  exposure: "Exposure population",
  financial: "Financial exposure",
  recurrence: "Recurrence",
  safety: "Safety / compliance",
  customer: "Customer impact",
  confidence: "Data confidence / readiness",
};

export const DEFAULT_PRIORITY_WEIGHTS: Record<PriorityFactor, number> = {
  severity: 0.22,
  trend: 0.12,
  exposure: 0.12,
  financial: 0.14,
  recurrence: 0.08,
  safety: 0.14,
  customer: 0.1,
  confidence: 0.08,
};

export interface PriorityBreakdown {
  score: number;
  factors: {
    key: PriorityFactor;
    label: string;
    value: number;
    weight: number;
    contribution: number;
  }[];
}

const clamp = (n: number) => Math.max(0, Math.min(1, n));

export function factorValues(s: Signal): Record<PriorityFactor, number> {
  const severity = s.currentSeverity ?? s.aiSuggestedSeverity ?? 2;
  return {
    severity: clamp(severity / 5),
    trend: s.trendDirection === "rising" ? 1 : s.trendDirection === "stable" ? 0.45 : 0.15,
    exposure: clamp(Math.log10(Math.max(s.exposureUnits, 1)) / 5),
    financial: clamp(Math.log10(Math.max(s.financialExposure, 1)) / 7.3),
    recurrence: clamp(s.recurrenceCount / 20),
    safety: s.safetyRelevant ? 1 : s.complianceRelevant ? 0.6 : 0,
    customer: clamp(s.customerImpact / 5),
    confidence: clamp(s.dataConfidence / 100),
  };
}

export function prioritise(
  s: Signal,
  weights: Record<PriorityFactor, number> = DEFAULT_PRIORITY_WEIGHTS,
): PriorityBreakdown {
  const values = factorValues(s);
  const total = Object.values(weights).reduce((a, b) => a + b, 0) || 1;
  const factors = (Object.keys(values) as PriorityFactor[]).map((key) => {
    const weight = (weights[key] ?? 0) / total;
    return {
      key,
      label: PRIORITY_FACTOR_LABELS[key],
      value: values[key],
      weight,
      contribution: values[key] * weight * 100,
    };
  });
  const score = Math.round(factors.reduce((a, f) => a + f.contribution, 0));
  return { score, factors };
}
